// src/pages/.../WorkerDetailsModal.jsx
import React from "react";
import {
  X, Pencil, Trash2, User2, Briefcase, Mail, Phone, MapPin, UserSquare2,
  ExternalLink
} from "lucide-react";
import CopyButton from "../../../../components/ui/Copybutton"; // 👈 ajusta el path si es distinto

const Row = ({ icon: Icon, label, children }) => (
  <div className="grid grid-cols-3 gap-3 py-2">
    <div className="flex items-center gap-2 text-sm text-gray-500">
      {Icon && <Icon size={16} className="opacity-70" />}
      {label}
    </div>
    <div className="col-span-2 text-sm text-gray-800 flex flex-wrap items-center gap-2">
      {children ?? <span className="text-gray-400">—</span>}
    </div>
  </div>
);

function escapeRegExp(s = "") {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export default function WorkerDetailsModal({
  open,
  onClose,
  worker,
  entityName,
  onEdit,
  onDelete,
}) {
  if (!open || !worker) return null;

  // ---- Dirección limpia (sin el nombre de la entidad al final) ----
  const rawAddress = (worker?.address || "").trim();
  const entity = (entityName || "").trim();

  const displayAddress = (() => {
    if (!rawAddress) return "";
    if (entity && rawAddress.toLowerCase().endsWith(entity.toLowerCase())) {
      // quita sufijos como "…, CORFO" o " CORFO"
      const pat = new RegExp(`[,\\s]*${escapeRegExp(entity)}$`, "i");
      return rawAddress.replace(pat, "").trim();
    }
    return rawAddress;
  })();

  const mapsHref = displayAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayAddress)}`
    : null;

  return (
    <div
      className="fixed inset-0 z-[230] bg-black/40 flex items-center justify-center p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.() }
    >
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 bg-gradient-to-r from-[#0c3b66] to-[#0488AF] text-white flex items-center justify-between">
          <div>
            <div className="font-semibold">{worker?.name}</div>
            <div className="text-sm opacity-90">
              {worker?.role || "Sin cargo"} · {entityName || "Entidad"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit?.(worker)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-xs font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors flex-shrink-0"
            >
              <Pencil size={16} /> Editar
            </button>
            <button
              onClick={() => onDelete?.(worker)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#C42251] text-white text-xs font-semibold hover:bg-[#A01D45] transition-colors flex-shrink-0"
            >
              <Trash2 size={16} /> Eliminar
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Cuerpo */}
        <div className="px-5 py-4">
          <Row icon={User2} label="Nombre">
            <span>{worker?.name}</span>
          </Row>

          <Row icon={Briefcase} label="Cargo">
            <span>{worker?.role || <span className="text-gray-400">—</span>}</span>
          </Row>
          
          {/* 👉 Correo con botón de copiar */}
          <Row icon={Mail} label="Correo">
            {worker?.email ? (
              <>
                <a
                  href={`mailto:${worker.email}`}
                  className="text-[#0488AF] hover:underline"
                >
                  {worker.email}
                </a>
                <CopyButton
                  text={worker.email}
                  label="Copiar correo"
                  className="ml-1 p-1 rounded hover:bg-gray-100 text-gray-600 border"
                  size={16}
                />
              </>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </Row>

          <Row icon={Phone} label="Teléfono">
            {worker?.phone ? (
              <a href={`tel:${worker.phone}`} className="text-[#0488AF] hover:underline">
                {worker.phone}
              </a>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </Row>

          <Row icon={Phone} label="Teléfono alternativo">
            {worker?.altPhone ? (
              <a href={`tel:${worker.altPhone}`} className="text-[#0488AF] hover:underline">
                {worker.altPhone}
              </a>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </Row>

          <Row icon={MapPin} label="Dirección">
            {displayAddress ? (
              <>
                <span>{displayAddress}</span>
                {mapsHref && (
                  <a
                    href={mapsHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[#0488AF] hover:underline"
                  >
                    <ExternalLink size={14} /> Ver en mapa
                  </a>
                )}
              </>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </Row>

          <Row icon={UserSquare2} label="Asistente/Secretaría">
            <span>{worker?.assistantName || <span className="text-gray-400">—</span>}</span>
          </Row>

          <Row icon={Phone} label="Teléfono asistente">
            {worker?.assistantPhone ? (
              <a href={`tel:${worker.assistantPhone}`} className="text-[#0488AF] hover:underline">
                {worker.assistantPhone}
              </a>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </Row>

          <Row icon={Mail} label="Correo asistente">
            {worker?.assistantEmail ? (
              <a href={`mailto:${worker.assistantEmail}`} className="text-[#0488AF] hover:underline">
                {worker.assistantEmail}
              </a>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </Row>
        </div>
      </div>
    </div>
  );
}
